import { AnvilProvider } from '@servicetitan/anvil2';
import { useInvariableEffect, useInitDependency } from '@servicetitan/cp-react-hooks';
import { DocumentTitle, PageLoader, portalStoreToken } from '@servicetitan/cp-ui';
import { CultureProvider } from '@servicetitan/culture';
import { DateTimeUtils } from '@servicetitan/datetime-utils';
import { LogService } from '@servicetitan/log-service';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import {
    AuthApi,
    BrandingApi,
    CustomerPortalEnvApi,
    CustomerPortalServiceAgreements,
    FeaturesApi,
    LegalAgreementApi,
    Memberships,
    MembershipsApi,
    SavedPaymentMethodsApi,
    ServiceAgreementsApi,
    SettingsApi,
} from 'api';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { observer } from 'mobx-react';
import { FC, StrictMode, useEffect } from 'react';
import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    To,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { AuthorizedApp } from './authorized.app';
import { FeatureFlagKeys } from './feature-flags';
import { AuthModule } from './modules/auth/components/auth.module';
import { GoogleIntermediateProvider } from './modules/auth/components/google';
import { LogoStore } from './modules/auth/stores/logo-store';
import { PortalAuthStore } from './modules/auth/stores/portal-auth.store';
import { useAuthHistory } from './modules/auth/utils/use-auth-history';
import { AppError } from './modules/common/components/app-error/app-error';
import { ColorPaletteThemeProvider } from './modules/common/components/layout/color-palette-theme-provider';
import { LDProvider } from './modules/common/components/ld-provider';
import { AppNotifications } from './modules/common/components/notifications/notifications';
import { AppSettingsStore } from './modules/common/stores/app-settings.store';
import { BillingAccessStore } from './modules/common/stores/billing-access.store';
import { ContractStore } from './modules/common/stores/contract.store';
import { EnvStore } from './modules/common/stores/env.store';
import { MembershipStore } from './modules/common/stores/membership.store';
import { WebPortalStore } from './modules/common/stores/portal.store';
import { ServiceAgreementStore } from './modules/common/stores/service-agreement.store';
import { SpecialSettingsStore } from './modules/common/stores/special-settings.store';
import {
    CustomBannerSettingsStore,
    PortalStartDateSettingsStore,
    ShowCompanyNameSettingsStore,
    SignInBannerSettingsStore,
} from './modules/dashboard/api';
import { SettingsStore } from './modules/dashboard/api/settings.store';
import { AppointmentDrawerStore } from './modules/dashboard/components/sections/jobs-dashboard/appointment-drawer/stores/appointment-drawer.store';
import { ServiceAgreementCoveredLocationsDrawerStore } from './modules/dashboard/components/sections/service-agreement-dashboard/service-agreement-drawer/stores/service-agreement-covered-locations-drawer.store';
import {
    ExpiredLinkPage,
    ExpiredLinkUrls,
} from './modules/expired-link/components/expired-link-page';
import { LegalAgreementGate } from './modules/legal-agreement/components/legal-agreement-gate';
import { LegalAgreementStore } from './modules/legal-agreement/stores/legal-agreement.store';
import './app.css';
/*
 * No local DI scope needed: SettingsApi, SpecialSettingsStore, AppSettingsStore, FeaturesApi,
 * SavedPaymentMethodsApi, PortalStartDateSettingsStore, ShowCompanyNameSettingsStore, and
 * LogoStore all resolve from App's top-level singleton list (below). AppSettingsStore in
 * particular must live there (not scoped to this component) so AuthModule — a routing sibling
 * of NotAuthorizedApp, not a descendant — can resolve the same instance for
 * ColorPaletteThemeProvider's mount at AppRouter (REQ-SET-PALETTE-003 AC1: sign-up/sign-in and
 * invite-flow buttons must re-theme too, and those routes render outside this component).
 */
export const NotAuthorizedApp: FC = observer(() => {
    const [{ authorized, authorizedLoginless, authorizing, init, redirect }, { loadingSettings }] =
        useDependencies(PortalAuthStore, AppSettingsStore);

    const authHistory = useAuthHistory();
    const location = useLocation();

    useInvariableEffect(() => {
        const path = location.pathname + location.search;

        init(path && path !== '/' ? path : undefined);
    });

    return authorizing || loadingSettings ? (
        <PageLoader />
    ) : authorized || authorizedLoginless ? (
        <LegalAgreementGate>
            <AuthorizedApp />
        </LegalAgreementGate>
    ) : (
        <Navigate
            to={authHistory.path(
                'login',
                undefined,
                redirect ? { redirect: encodeURI(redirect) } : undefined
            )}
        />
    );
});

export const AppRouter = observer(() => {
    const [{ onNavigate }, { colorPaletteShades }] = useDependencies<
        [WebPortalStore, AppSettingsStore]
    >(portalStoreToken as any, AppSettingsStore);
    const navigate = useNavigate();
    const { enablePortalTheme } = useFlags<FeatureFlagKeys>();

    useEffect(() => {
        onNavigate((to: To) => {
            navigate(to);
        });

        return () => {
            onNavigate();
        };
    }, [navigate, onNavigate]);

    /*
     * Mounted here — above both routing branches — rather than inside PortalLayout, because
     * AuthModule (/auth/* : login, register/sign-up, invite, recovery, mfa) is a routing sibling
     * of NotAuthorizedApp, not a descendant of PortalLayout. REQ-SET-PALETTE-003 AC1 requires
     * sign-up/sign-in and invite-flow buttons to re-theme too, so the single ThemeProvider mount
     * (ARCH-SET-PALETTE-001, task 9's architecture constraint) has to sit above both branches.
     *
     * Gated on enablePortalTheme rather than only hiding the tenant-settings selector: a tenant
     * who picked a family before the flag was turned off (or before it's rolled out to them)
     * must still see today's plain default-blue theme everywhere, not a stale saved selection.
     */
    return (
        <ColorPaletteThemeProvider shades={enablePortalTheme ? colorPaletteShades : undefined}>
            <Routes>
                <Route path="/auth/*" element={<AuthModule />} />
                {/*
                 * Expired loginless links are redirected here without any auth or frontend
                 * cookie, so these must render outside NotAuthorizedApp, which would otherwise
                 * send the customer to the login page.
                 */}
                <Route
                    path={ExpiredLinkUrls.invoice}
                    element={<ExpiredLinkPage type="invoice" />}
                />
                <Route
                    path={ExpiredLinkUrls.proposal}
                    element={<ExpiredLinkPage type="proposal" />}
                />
                <Route path="*" element={<NotAuthorizedApp />} />
            </Routes>
        </ColorPaletteThemeProvider>
    );
});

const AppUnwrapped = () => {
    return (
        <LDProvider>
            <GoogleIntermediateProvider>
                <AppNotifications />
                <BrowserRouter>
                    <AppRouter />
                </BrowserRouter>
            </GoogleIntermediateProvider>
        </LDProvider>
    );
};

export const App: FC = provide({
    singletons: [
        AuthApi,
        LegalAgreementApi,
        LegalAgreementStore,
        DateTimeUtils,
        PortalAuthStore,
        { provide: portalStoreToken, useClass: WebPortalStore },
        CustomerPortalEnvApi,
        EnvStore,
        AppointmentDrawerStore,
        ServiceAgreementCoveredLocationsDrawerStore,
        BrandingApi,
        SettingsStore,
        MembershipStore,
        MembershipsApi,
        Memberships,
        ServiceAgreementStore,
        ServiceAgreementsApi,
        CustomerPortalServiceAgreements,
        SettingsApi,
        SpecialSettingsStore,
        AppSettingsStore,
        FeaturesApi,
        SavedPaymentMethodsApi,
        PortalStartDateSettingsStore,
        ShowCompanyNameSettingsStore,
        CustomBannerSettingsStore,
        SignInBannerSettingsStore,
        LogoStore,
        BillingAccessStore,
        ContractStore,
    ],
})(() => {
    const { culture, settings } = useInitDependency<WebPortalStore>(portalStoreToken as any);

    return (
        <CultureProvider value={culture}>
            <StrictMode>
                <LogService endpoint="/api/log" storageKey="LogStorage_CustomerPortal">
                    {/*
                     * Anvil2 v3 scopes every component's CSS under a `.anvil2` root class
                     * (v2 shipped it unscoped). AnvilProvider applies that class (and the
                     * `anvil2` body class for portaled content like toasts), so without it
                     * anvil2 components — e.g. the Grid-based portal layout — lose
                     * `display: grid` and collapse to block flow. Mirrors each MFE's root.
                     */}
                    <AnvilProvider>
                        <DocumentTitle title={settings?.title ?? ''} />
                        {settings ? <AppUnwrapped /> : <AppError />}
                    </AnvilProvider>
                </LogService>
            </StrictMode>
        </CultureProvider>
    );
});
